import { Plus, GripVertical, MoreVertical, ChevronUp, ChevronDown, ChevronsUp, ChevronsDown } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuLabel } from "@/components/ui/dropdown-menu";
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import TaskCard from "./TaskCard";

// Sortable wrapper for category cards
const SortableCategoryWrapper = ({ domain, children, isDragging }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
  } = useSortable({ id: domain.key });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    zIndex: isDragging ? 1000 : 1,
  };

  return (
    <div ref={setNodeRef} style={style} {...attributes}>
      {children({ listeners })}
    </div>
  );
};

const CategoryColumn = ({
  domain,
  domainTasks,
  activeDragId,
  isDraggingTask,
  dropTargetCategory,
  dropTargetTaskId,
  dragItem,
  onAddTask,
  onCompleteTask,
  onEditTask,
  onDeleteTask,
  onTogglePriority,
  onMoveTask,
  onMoveCategoryToTop,
  onMoveCategoryUp,
  onMoveCategoryDown,
  onMoveCategoryToBottom,
  onTaskDragStart,
  onTaskDragEnd,
  onTaskDragOver,
  onTaskDropOnTask,
  onCategoryAreaDragOver,
  onCategoryAreaDrop,
  setDropTargetCategory
}) => {
  const isCustom = domain.type === 'custom';
  const isBeingDragged = activeDragId === domain.key;
  const isTaskDropTarget = dropTargetCategory === domain.key && isDraggingTask;
  
  return (
    <SortableCategoryWrapper domain={domain} isDragging={isBeingDragged}>
      {({ listeners }) => (
        <div 
          className={`bg-white rounded-2xl border shadow-sm overflow-hidden transition-all duration-200 ${
            isBeingDragged ? 'ring-2 ring-indigo-500 shadow-lg scale-[1.02]' : ''
          } ${isTaskDropTarget ? 'ring-2 ring-green-500 bg-green-50/30' : ''}`}
        >
          {/* Header con drag handle e menu azioni */}
          <div className={`bg-gradient-to-r ${domain.gradient} px-4 py-3 border-b border-gray-100`}>
            <div className="flex items-center justify-between">
              <div 
                className="flex items-center gap-2 flex-1 cursor-grab active:cursor-grabbing"
                {...listeners}
              >
                <GripVertical className="w-4 h-4 text-gray-400" />
                <span className="text-xl">{domain.emoji}</span>
                <span className="font-semibold text-gray-700">{domain.label}</span>
                <Badge variant="secondary" className="text-xs rounded-full px-2 bg-white/70">
                  {domainTasks.length}
                </Badge>
              </div>
              
              <div className="flex items-center gap-1">
                {/* Menu azioni rapide */}
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <button className="w-7 h-7 rounded-full bg-white/60 text-gray-500 flex items-center justify-center hover:bg-white hover:shadow-sm transition-all">
                      <MoreVertical className="w-4 h-4" />
                    </button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-44 rounded-xl">
                    <DropdownMenuLabel className="text-xs text-gray-400">Sposta categoria</DropdownMenuLabel>
                    <DropdownMenuItem onClick={() => onMoveCategoryToTop(domain.key)} className="cursor-pointer rounded-lg text-sm">
                      <ChevronsUp className="w-4 h-4 mr-2" /> In cima
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => onMoveCategoryUp(domain.key)} className="cursor-pointer rounded-lg text-sm">
                      <ChevronUp className="w-4 h-4 mr-2" /> Su di uno
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => onMoveCategoryDown(domain.key)} className="cursor-pointer rounded-lg text-sm">
                      <ChevronDown className="w-4 h-4 mr-2" /> Giù di uno
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => onMoveCategoryToBottom(domain.key)} className="cursor-pointer rounded-lg text-sm">
                      <ChevronsDown className="w-4 h-4 mr-2" /> In fondo
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
                
                <button
                  onClick={() => onAddTask(isCustom ? "altro" : domain.key, isCustom ? domain.name : "")}
                  className="w-7 h-7 rounded-full bg-white/80 shadow-sm text-gray-500 flex items-center justify-center hover:bg-white hover:shadow-md transition-all"
                >
                  <Plus className="w-4 h-4" />
                </button>
              </div>
            </div>
          </div>
          
          {/* Tasks - DROP ZONE */}
          <div 
            className={`p-3 space-y-2 min-h-[80px] max-h-80 overflow-y-auto transition-colors ${
              isTaskDropTarget ? 'bg-green-50/50' : ''
            }`}
            onDragOver={(e) => onCategoryAreaDragOver(e, domain.key)}
            onDragLeave={() => setDropTargetCategory(null)}
            onDrop={(e) => onCategoryAreaDrop(e, domain.key)}
          >
            {domainTasks.length > 0 ? (
              <>
                {domainTasks.map(task => {
                  const isTaskBeingDragged = isDraggingTask && dragItem.current?.task?.task_id === task.task_id;
                  const isTaskDropTargetItem = dropTargetTaskId === task.task_id;
                  
                  return (
                    <TaskCard
                      key={task.task_id}
                      task={task}
                      domainKey={domain.key}
                      domainTasks={domainTasks}
                      isDragging={isTaskBeingDragged}
                      isDropTarget={isTaskDropTargetItem}
                      onComplete={onCompleteTask}
                      onEdit={onEditTask}
                      onDelete={onDeleteTask}
                      onTogglePriority={onTogglePriority}
                      onMove={onMoveTask}
                      onDragStart={onTaskDragStart}
                      onDragEnd={onTaskDragEnd}
                      onDragOver={onTaskDragOver}
                      onDrop={onTaskDropOnTask}
                    />
                  );
                })}
              </>
            ) : (
              <div className="text-center py-6">
                <p className="text-sm text-gray-400 mb-2">Nessun compito</p>
                <button 
                  onClick={() => onAddTask(isCustom ? "altro" : domain.key, isCustom ? domain.name : "")}
                  className="text-xs text-indigo-600 hover:text-indigo-800 font-medium"
                >
                  + Aggiungi compito
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </SortableCategoryWrapper>
  );
};

export default CategoryColumn;
